import { useState } from 'react'
import {
  useDeleteIntranetIncident,
  useFollowUpIntranetIncident,
  useIntranetIncidentMeta,
  useIntranetIncidents,
  useReportIntranetIncident
} from '../api/queries'
import type { IntranetIncident, IntranetIncidentKind, IntranetIncidentStatus } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { useCreateRequest } from '../lib/createRequest'
import { formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

const KINDS: { key: IntranetIncidentKind; label: string; hint: string }[] = [
  { key: 'near_miss', label: 'Nærved-ulykke', hint: 'Det gik lige - men kunne være endt med en skade' },
  { key: 'unsafe_condition', label: 'Farlig situation', hint: 'Noget farligt, der venter på at gå galt' },
  { key: 'accident', label: 'Ulykke', hint: 'Nogen kom til skade' }
]

const STATUS_COLORS: Record<IntranetIncidentStatus, string> = {
  new: '#DC2626',
  investigating: '#D97706',
  closed: '#16A34A'
}

/** "YYYY-MM-DDTHH:mm" i lokal tid - det, et datetime-local-felt vil have. */
function localNow() {
  const now = new Date()
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
  return now.toISOString().slice(0, 16)
}

/**
 * Fanen "Arbejdsmiljø" (docs/DECISIONS.md beslutning 113): meld en nærved-hændelse, en farlig situation eller en
 * ulykke - også anonymt. Dem, der tager imod, ser alle og følger op; andre ser kun dem, de selv har meldt med navn.
 */
export function IncidentsTab() {
  const { data: meta } = useIntranetIncidentMeta()
  const [includeClosed, setIncludeClosed] = useState(false)
  const { data, isLoading, error } = useIntranetIncidents(includeClosed)
  const [reporting, setReporting] = useState(false)
  const [thanks, setThanks] = useState<string | null>(null)

  useCreateRequest(['naerved'], () => {
    setThanks(null)
    setReporting(true)
  })

  const canHandle = meta?.canHandle ?? false

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <label className="row small muted" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
          />
          Vis også lukkede
        </label>

        {!reporting && (
          <button
            className="btn btn--primary"
            onClick={() => {
              setThanks(null)
              setReporting(true)
            }}
          >
            Meld en hændelse
          </button>
        )}
      </div>

      {thanks && <div className="map-notice">{thanks}</div>}

      {reporting && (
        <ReportForm
          onClose={() => setReporting(false)}
          onReported={(anonymous) => {
            setReporting(false)
            setThanks(
              anonymous
                ? 'Tak! Hændelsen er meldt anonymt - der står ikke noget om, hvem der meldte den.'
                : 'Tak! Hændelsen er meldt. Du får besked, når der er fulgt op.'
            )
          }}
        />
      )}

      {canHandle && meta && !meta.hasReceivingGroup && (
        <div className="small muted">
          Ingen arbejdsgruppe tager imod hændelserne endnu, så de går til kontoret og administratorerne. Sæt flueben
          i "Tager imod nærved-hændelser" på en gruppe under Arbejdsgrupper.
        </div>
      )}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && data.length === 0 && (
        <EmptyState>
          {canHandle
            ? 'Ingen åbne hændelser.'
            : 'Du har ikke meldt noget med dit navn. Så du noget, der kunne være gået galt, så tryk "Meld en hændelse" - gerne anonymt.'}
        </EmptyState>
      )}

      {data?.map((incident) => (
        <IncidentCard key={incident.id} incident={incident} />
      ))}
    </div>
  )
}

/** Meldingen. Billederne sendes med i samme kald - så en anonym melding aldrig skal findes igen. */
function ReportForm({ onClose, onReported }: { onClose: () => void; onReported: (anonymous: boolean) => void }) {
  const report = useReportIntranetIncident()
  const [kind, setKind] = useState<IntranetIncidentKind>('near_miss')
  const [occurredAt, setOccurredAt] = useState(localNow)
  const [location, setLocation] = useState('')
  const [description, setDescription] = useState('')
  const [anonymous, setAnonymous] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      const photos = []
      for (const file of files.slice(0, 6)) {
        photos.push(await prepareDocumentFile(file))
      }

      await report.mutateAsync({
        kind,
        occurredAt: occurredAt ? new Date(occurredAt).toISOString() : null,
        location: location.trim(),
        description: description.trim(),
        isAnonymous: anonymous,
        photos
      })
      onReported(anonymous)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hændelsen kunne ikke meldes.')
    }
  }

  return (
    <Card title="Meld en hændelse">
      <form className="stack" onSubmit={submit}>
        <div className="field">
          <span className="field__label">Hvad er sket? *</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {KINDS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`btn btn--sm ${kind === item.key ? 'btn--primary' : ''}`}
                title={item.hint}
                onClick={() => setKind(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <span className="tiny muted">{KINDS.find((k) => k.key === kind)?.hint}</span>
          {kind === 'accident' && (
            <span className="tiny warn">
              En alvorlig ulykke skal stadig anmeldes til Arbejdstilsynet - det gør denne melding ikke.
            </span>
          )}
        </div>

        <div className="form-grid">
          <label className="field">
            <span className="field__label">Hvornår *</span>
            <input type="datetime-local" required value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Hvor *</span>
            <input
              required
              maxLength={300}
              placeholder="F.eks. Industrivej 12, tavlerummet"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </label>
        </div>

        <label className="field">
          <span className="field__label">Hvad skete der - og hvad kunne være sket? *</span>
          <textarea
            required
            maxLength={4000}
            rows={4}
            placeholder="F.eks. Stigen skred på det våde gulv, da jeg stod på tredje trin"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">
            Billeder {files.length > 0 && <span className="tiny muted">({files.length} valgt, højst 6)</span>}
          </span>
          <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
        </label>

        <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginTop: 3 }}
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          />
          <span>
            Meld anonymt
            <span className="tiny muted" style={{ display: 'block' }}>
              Så gemmes der intet om, hvem du er - heller ikke i loggen. Du kan til gengæld ikke se meldingen bagefter
              eller få besked om opfølgningen. Skriv ikke noget i teksten, der afslører dig, hvis du vil være anonym.
            </span>
          </span>
        </label>

        {error && <div className="error-box">{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={report.isPending}>
            {report.isPending ? 'Sender ...' : anonymous ? 'Meld anonymt' : 'Meld hændelsen'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

/** Én hændelse med opfølgning. */
function IncidentCard({ incident }: { incident: IntranetIncident }) {
  const { isIntranetAdmin } = useAuth()
  const [following, setFollowing] = useState(false)
  const remove = useDeleteIntranetIncident()

  async function deleteIncident() {
    if (!window.confirm('Slet hændelsen? Brug kun det til en fejlmelding - ellers så luk den i stedet.')) return
    try {
      await remove.mutateAsync(incident.id)
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Hændelsen kunne ikke slettes.')
    }
  }

  return (
    <article className="card card--flat" style={{ padding: 14, borderLeft: `4px solid ${STATUS_COLORS[incident.status]}` }}>
      <div className="row row--between" style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>
            {incident.kindName} · {incident.location}
          </div>
          <div className="tiny muted">
            Skete {formatDateTime(incident.occurredAt)} · meldt{' '}
            {incident.isAnonymous ? 'anonymt' : `af ${incident.reportedBy?.displayName ?? 'en tidligere kollega'}`}
            {incident.projectName && <> · {incident.projectName}</>}
          </div>
        </div>
        <span
          className="pill"
          style={{ background: STATUS_COLORS[incident.status], color: '#fff', flexShrink: 0 }}
        >
          {incident.statusName}
        </span>
      </div>

      <div className="small" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
        {incident.description}
      </div>

      {incident.photos.length > 0 && (
        <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {incident.photos.map((photo) => (
            <IncidentPhoto key={photo.id} url={photo.url} />
          ))}
        </div>
      )}

      {incident.followUp && !following && (
        <div className="small" style={{ marginTop: 10, padding: 10, background: '#F8FAFC', borderRadius: 8 }}>
          <strong>Opfølgning:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{incident.followUp}</span>
          {incident.handledBy && <div className="tiny muted">{incident.handledBy.displayName}</div>}
        </div>
      )}

      {following && <FollowUpForm incident={incident} onClose={() => setFollowing(false)} />}

      {incident.canFollowUp && !following && (
        <div className="btn-group" style={{ marginTop: 10 }}>
          <button className="btn btn--sm btn--primary" onClick={() => setFollowing(true)}>
            Følg op
          </button>
          {isIntranetAdmin && (
            <button className="btn btn--sm" disabled={remove.isPending} onClick={() => void deleteIncident()}>
              Slet
            </button>
          )}
        </div>
      )}
    </article>
  )
}

function FollowUpForm({ incident, onClose }: { incident: IntranetIncident; onClose: () => void }) {
  const followUp = useFollowUpIntranetIncident()
  const [status, setStatus] = useState<IntranetIncidentStatus>(
    incident.status === 'new' ? 'investigating' : incident.status
  )
  const [text, setText] = useState(incident.followUp ?? '')
  const [error, setError] = useState<string | null>(null)

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await followUp.mutateAsync({ id: incident.id, status, followUp: text })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opfølgningen kunne ikke gemmes.')
    }
  }

  return (
    <form className="stack" style={{ marginTop: 10 }} onSubmit={save}>
      <label className="field">
        <span className="field__label">Status</span>
        <select value={status} onChange={(e) => setStatus(e.target.value as IntranetIncidentStatus)}>
          <option value="new">Ny</option>
          <option value="investigating">Undersøges</option>
          <option value="closed">Lukket</option>
        </select>
      </label>
      <label className="field">
        <span className="field__label">Hvad er gjort ved det?{status === 'closed' ? ' *' : ''}</span>
        <textarea
          rows={3}
          maxLength={4000}
          required={status === 'closed'}
          placeholder="F.eks. Skridsikre måtter bestilt til alle tavlerum, stigerne tjekket"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {error && <div className="error-box">{error}</div>}
      <div className="btn-group">
        <button className="btn btn--sm btn--primary" type="submit" disabled={followUp.isPending}>
          {followUp.isPending ? 'Gemmer ...' : 'Gem'}
        </button>
        <button className="btn btn--sm" type="button" onClick={onClose}>
          Fortryd
        </button>
      </div>
    </form>
  )
}

function IncidentPhoto({ url }: { url: string }) {
  const blob = useAuthedBlob(url)
  if (!blob) return <div style={{ width: 88, height: 88, borderRadius: 6, background: '#F1F5F9' }} />
  return (
    <a href={blob} target="_blank" rel="noreferrer">
      <img src={blob} alt="Billede fra hændelsen" style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 6 }} />
    </a>
  )
}
